import { Box } from "@mui/material";
import FreeShippingIndicator from "../FreeShippingIndicator";
import PercentageChip from "../PercentageChip";
import EditNumberInput from "../EditNumberInput";
import PublicationState from "../PublicationState";
import HoverDetailCard from "../HoverDetailCard";
import CustomDetailPanelToggle from "../CustomDetailPanelToggle";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import LCUSDField from "../LCUSDField";
import { formatearNumeroConComas } from "views/Components/Shared/utils/formatNumbers";
import PvpHeader from "../PvpHeader";
import PvpShippingCell from "../PvpShippingCell";
import ColumnNumberField from "../ColumnNumberField";
import ProjectedFreeShipping from "../ProjectedFreeShipping";
import ProjectedPercentageField from "../ProjectedPercentageField";
import ProjectedPvpField from "../ProjectedPvpField";
import EmptyTable from "views/Components/Shared/Components/EmptyTable";
import ThumbnailWithIcon from "../ThumbnailWithIcon";
import ListingTypeBadgeHeader from "../ListingTypeBadgeHeader";
import ListingTypeBadge from "../../DetailPanel/ListingTypeBadge";
import BuyBoxAlert from "views/Components/Shared/Components/BuyBox/BuyBoxAlert";

export function getRowId(row) {
  return row.id;
}

// Calcula los datos proyectados de la fila.
// Si se pasa `meliListCostOverride` (costo con IVA, obtenido desde MELI vía backend),
// y el PVP proyectado cruza basefreeshipping con costos base 0/falsy, ese valor
// sustituye al de la fila. El override se rutea a free shipping cuando la fila
// tiene `freeshipping=true`, o a fixrate cuando es false (MX/CO con costo operativo).
export const calculateProyectedData = ({
  usdcost_proyectado,
  pvpdisbyte_proyectado,
  dolar,
  pais,
  row,
  meliListCostOverride,
  // Alias retro-compatible: si se llama con freeShippingGrossOverride se sigue aceptando.
  freeShippingGrossOverride,
}) => {
  const override = meliListCostOverride ?? freeShippingGrossOverride;
  const costo =
    usdcost_proyectado && usdcost_proyectado !== 0
      ? usdcost_proyectado * dolar
      : row.localcost;

  //const varProyPriceNoIva = pvpdisbyte_proyectado / (1 + row.iva); //ok

  let varProyPriceNoIva;
  if (pais === "ARGENTINA") {
    varProyPriceNoIva = pvpdisbyte_proyectado / (1 + row.ivaaplicado); //ok
  } else {
    varProyPriceNoIva = pvpdisbyte_proyectado / (1 + row.iva); //ok
  }

  const varProyMeliFeeAdjNoIva =
    (row.melifeeadjperc * pvpdisbyte_proyectado) / (1 + row.iva); // ok

  // Sin un basefreeshipping > 0 conocido, no podemos decir que se "cruzó" el umbral.
  // (JS evalúa `n >= null` como true porque null coerciona a 0, lo cual aplicaba el
  // override de MELI incluso con el proyectado por debajo del umbral real.)
  const baseFsThreshold = Number(row.basefreeshipping) || 0;
  const crossesFreeShipping =
    baseFsThreshold > 0 && pvpdisbyte_proyectado >= baseFsThreshold;
  const hasOverride = override != null && override > 0;
  // Decide cómo enrutar el override de MELI:
  //  - MX/CO: distinguimos por row.freeshipping. Si la fila tiene freeshipping=true,
  //    el override va a FS (cuando cruza umbral); si freeshipping=false, va a fixrate
  //    (operating cost, regardless of umbral).
  //  - ARG / BRA / otros: el override siempre representa free shipping. Aplica cuando
  //    el PVP proyectado cruza el umbral, independientemente de si la fila venía con
  //    freeshipping=true o false en la BD (proyectamos cómo quedaría si se guardara).
  const isMxOrCo = pais === "COLOMBIA" || pais === "MEXICO";
  const routesAsFreeShipping = isMxOrCo ? row.freeshipping === true : true;

  // Free shipping efectivo. Solo aplica cuando la fila tiene freeshipping=true.
  // El override de MELI (si llegó) tiene prioridad sobre el costo cacheado en la fila:
  // si pedimos a MELI es porque queremos el valor fresco, así que siempre gana.
  let effectiveFreeShippingGross = row.freeshippingcostc || 0;
  let effectiveFreeShippingNoIva = row.freeshippingcostnoivac || 0;
  if (routesAsFreeShipping && crossesFreeShipping && hasOverride) {
    effectiveFreeShippingGross = override;
    effectiveFreeShippingNoIva = override / (1 + row.iva);
  }

  let varProyFreeShippingNoIva =
    row.shippingmode === "me1" && !row.freeshipping
      ? 0
      : routesAsFreeShipping && crossesFreeShipping
      ? effectiveFreeShippingNoIva
      : 0; // ok

  // Amazon: el FBA fee es un costo de envío fijo por producto (independiente del PVP y sin
  // umbral basefreeshipping). Se rutea SIEMPRE a free shipping —nunca a fixrate—; si el
  // listing no tiene envío gratis habilitado, no se cobra.
  const isAmazon = row.site === "AMZN";
  if (isAmazon) {
    varProyFreeShippingNoIva =
      row.freeshipping === false ? 0 : row.freeshippingcostnoivar || 0;
  }

  let varProyFixRate;

  if (crossesFreeShipping && routesAsFreeShipping) {
    // Cruza el umbral con freeshipping=true → el costo entra como FS, fixrate=0.
    varProyFixRate = 0;
  } else if (isMxOrCo) {
    // MX/CO: MELI es la verdad absoluta para el fixrate (sea operating cost cuando
    // freeshipping=false, o el costo por debajo del umbral cuando freeshipping=true).
    // El override de MELI manda; sino caemos al valor cacheado.
    if (hasOverride) {
      varProyFixRate = override / (1 + row.iva);
    } else if (row.freeshipping === false && row.fixrate && row.fixrate > 0) {
      // Operating cost ya movido al fixrate por el mapper para MX/CO + freeshipping=false.
      varProyFixRate = row.fixrate / (1 + row.iva);
    } else {
      // Fallback: costo cacheado en freeshippingcostnoivar (cuando freeshipping=true).
      varProyFixRate = row.freeshippingcostnoivar || 0;
    }
  } else if (pais === "ARGENTINA") {
    // ARG mantiene su lógica tiered por rangos (sin override de MELI).
    if (baseFsThreshold > 0 && pvpdisbyte_proyectado < baseFsThreshold) {
      varProyFixRate =
        pvpdisbyte_proyectado <= 15999
          ? row.basefixrate / (1 + row.iva)
          : pvpdisbyte_proyectado <= 23999
          ? row.basefixrate2 / (1 + row.iva)
          : pvpdisbyte_proyectado <= 33000
          ? row.basefixrate3 / (1 + row.iva)
          : 0;
    } else {
      varProyFixRate = 0;
    }
  } else {
    // BRA y otros: lógica original.
    if (baseFsThreshold > 0 && pvpdisbyte_proyectado < baseFsThreshold) {
      varProyFixRate = row.basefixrate / (1 + row.iva);
    } else {
      varProyFixRate = 0;
    }
  }

  varProyFixRate = row.site === "VTEX" || isAmazon ? 0 : varProyFixRate;

  let varProyProfit =
    varProyPriceNoIva -
    varProyMeliFeeAdjNoIva -
    varProyFixRate -
    varProyFreeShippingNoIva -
    costo;

  if (pais === "ARGENTINA") {
    // FALTA
    // let montoIvaAplicado = varProyProfit * row.iva_aplicado;
    // varProyProfit = proyPVPBUYER - montoIvaAplicado - row.actiibb

    const pvpIcaCalculated = varProyPriceNoIva * row.iibb;
    varProyProfit = varProyProfit - pvpIcaCalculated;
  }

  if (pais === "COLOMBIA") {
    //varProyProfit = varProyProfit - (row.pvpica ? row.pvpica : 0);
    const pvpIcaCalculated = varProyPriceNoIva * row.ica;
    varProyProfit = varProyProfit - pvpIcaCalculated;
  }

  let pvp_proyectado =
    pvpdisbyte_proyectado + (row.pvp * (row.pvp - row.pvpdisbyte)) / row.pvp;
  pvp_proyectado = pvp_proyectado ? pvp_proyectado : 0;

  const pvpbuyer_proyectado =
    pvpdisbyte_proyectado - row.pvp * (row.rebateperc / 100);

  // Free shipping para la UI (flag + gross con IVA). Amazon usa el FBA fee fijo;
  // el resto respeta el cruce de umbral / me1.
  const proyFreeShippingFlag = isAmazon
    ? row.freeshipping === true
    : row.shippingmode === "me1" && !row.freeshipping
    ? false
    : crossesFreeShipping;

  const proyFreeShippingGross = isAmazon
    ? row.freeshipping === false
      ? 0
      : row.freeshippingcostc || 0
    : row.shippingmode === "me1" && !row.freeshipping
    ? 0
    : crossesFreeShipping
    ? effectiveFreeShippingGross
    : 0;

  const newData = {
    localcost_proyectado:
      usdcost_proyectado > 0 ? +(usdcost_proyectado * dolar).toFixed(2) : 0,
    margen_proyectado: +((varProyProfit / varProyPriceNoIva) * 100).toFixed(2),
    roi_proyectado: +((varProyProfit / costo) * 100).toFixed(2),
    freeshipping_proyectado: proyFreeShippingFlag,
    // (row.pvpdisbyte < row.basefreeshipping &&
    // 	pvpdisbyte_proyectado >= row.basefreeshipping) ||
    // (row.pvpdisbyte >= row.basefreeshipping &&
    // 	pvpdisbyte_proyectado < row.basefreeshipping),
    pvp_proyectado,
    pvpbuyer_proyectado,
    discount_proyectado:
      pvp_proyectado && pvpbuyer_proyectado
        ? +(
            ((pvp_proyectado - pvpbuyer_proyectado) / pvp_proyectado) *
            100 *
            -1
          ).toFixed(2)
        : 0.0,
    discount: Number.isFinite(+((row.baseprice / row.pvp - 1) * 100).toFixed(2))
      ? +((row.baseprice / row.pvp - 1) * 100).toFixed(2)
      : 0,
    costo_proyectado: costo,
    pvpdisbytenoiva_proyectado: varProyPriceNoIva,
    melifeeadjnoiva_proyectado: varProyMeliFeeAdjNoIva,
    freeshippingnoiva_proyectado: varProyFreeShippingNoIva,
    fixratenoiva_proyectado: varProyFixRate,
    profit_proyectado: varProyProfit,
    // Costo con IVA efectivo usado para la proyección. Si veníamos sin valor base y
    // se aplicó el override de MELI, queda reflejado para que la UI muestre el dato.
    freeshippingcostc_proyectado: proyFreeShippingGross,
  };

  return newData;
};

export const columns = [
  // {
  // 	// ...GRID_CHECKBOX_SELECTION_COL_DEF,
  // 	field: '',
  // 	headerName: '',
  // 	cellClassName: (params) =>
  // 		`${
  // 			params?.row?.catalogflag && params?.row?.hijacked
  // 				? 'cell-hij'
  // 				: params?.row?.catalogflag
  // 				? 'cell-cat'
  // 				: ''
  // 		} no-padding`,
  // 	renderCell: (params) => <CustomCheckbox row={params.row} />,
  // 	headerClassName: 'header border-bottom',
  // 	width: 62,
  // 	resizable: false,
  // 	headerAlign: 'center',
  // 	align: 'center',
  // },
  {
    ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
    headerClassName: "header border-bottom",
    cellClassName: (params) =>
      `${
        params?.row?.catalogflag && params?.row?.hijacked
          ? "cell-hij"
          : params?.row?.catalogflag
          ? "cell-cat"
          : ""
      } no-padding`,
    renderCell: (params) => (
      <CustomDetailPanelToggle
        id={params.id}
        value={params.value}
        row={params.row}
      />
    ),
    width: 55,
    resizable: false,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "thumbnail",
    headerName: "Foto",
    headerClassName: "header border-bottom",
    cellClassName: (params) =>
      `${
        params?.row?.catalogflag && params?.row?.hijacked
          ? "cell-hij"
          : params?.row?.catalogflag
          ? "cell-cat"
          : ""
      }`,
    renderCell: (params) => (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <HoverDetailCard item={params.row}>
          <Box>
            <ThumbnailWithIcon src={params.value} item={params.row} />
          </Box>
        </HoverDetailCard>
      </Box>
    ),
    width: 80,
    height: 80,
    resizable: false,
    sortable: false,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "sku",
    headerName: "SKU",
    headerClassName: "header border-bottom",
    cellClassName: (params) =>
      `${
        params?.row?.catalogflag && params?.row?.hijacked
          ? "cell-hij"
          : params?.row?.catalogflag
          ? "cell-cat"
          : ""
      }`,
    minWidth: 90,
    flex: 1,
    resizable: false,
    headerAlign: "center",
    align: "center",
  },
  {
    // Buy Box: ⚠️ cuando la publicación está perdiendo la competencia de catálogo.
    field: "buybox",
    headerName: "Buy Box",
    headerClassName: "header border-bottom",
    renderCell: (params) => (
      <BuyBoxAlert
        competition={params.row?.catalogcompetition}
        catalogFlag={params.row?.catalogflag}
        title={params.row?.title || params.row?.sku}
      />
    ),
    width: 80,
    resizable: false,
    sortable: false,
    headerAlign: "center",
    align: "center",
  },
  // {
  // 	field: 'channel',
  // 	headerName: 'Canal',
  // 	headerClassName: 'header border-bottom',
  // 	minWidth: 90,
  // 	renderCell: (params) => {
  // 		return (
  // 			<Box
  // 				sx={{
  // 					display: 'flex',
  // 					alignItems: 'center',
  // 					justifyContent: 'center',
  // 					height: '100%',
  // 				}}
  // 			>
  // 				<ChannelIcon />
  // 			</Box>
  // 		);
  // 	},
  // 	flex: 1,
  // 	resizable: false,
  // 	headerAlign: 'center',
  // 	align: 'center',
  // },
  {
    field: "status",
    headerName: "Estado",
    renderCell: (params) => {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
          }}
        >
          <PublicationState status={params.value} />
        </Box>
      );
    },
    cellClassName: (params) =>
      `${
        params?.row?.catalogflag && params?.row?.hijacked
          ? "cell-hij"
          : params?.row?.catalogflag
          ? "cell-cat"
          : ""
      }`,
    headerClassName: "header border-bottom",
    width: 100,
    resizable: false,
    sortable: false,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "listingtypename",
    headerName: "Tipo de publicación",
    headerClassName: "header border-bottomr",
    renderHeader: () => <ListingTypeBadgeHeader />,
    renderCell: (params) => (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
        }}
      >
        <ListingTypeBadge
          listingType={params?.row?.listingtypename}
          tags={params.tags}
        />
      </Box>
    ),
    cellClassName: (params) =>
      `${
        params?.row?.catalogflag && params?.row?.hijacked
          ? "cell-hij"
          : params?.row?.catalogflag
          ? "cell-cat"
          : ""
      }`,
    minWidth: 120,
    flex: 1,
    resizable: false,
    sortable: false,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "usdcost",
    headerName: "LC USD",
    headerClassName: "header border-bottom border-left",
    renderCell: (params) => (
      <LCUSDField value={params.value} row={params.row} />
    ),
    cellClassName: "border-left",
    valueFormatter: (value) => {
      if (value == null) {
        return "";
      }
      return `$${formatearNumeroConComas(value)}`;
    },
    type: "number",
    minWidth: 90,
    flex: 1,
    resizable: false,
    sortable: false,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "localcost",
    headerName: "LC Local",
    headerClassName: "header border-bottom",
    valueFormatter: (value) => {
      if (value == null) {
        return "";
      }
      return `$${formatearNumeroConComas(value)}`;
    },
    type: "number",
    minWidth: 90,
    flex: 1,
    resizable: false,
    sortable: false,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "pvp",
    headerName: "PVP + Shipping",
    headerClassName: "header border-bottom",
    renderHeader: () => <PvpHeader />,
    renderCell: (params) => (
      <PvpShippingCell
        pvp={params.value}
        freeshipping={params.row.freeshipping}
        freeshippingCost={params.row.freeshippingcostr}
      />
    ),
    type: "number",
    minWidth: 120,
    flex: 1,
    resizable: false,
    sortable: false,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "pvpdisbyte",
    headerName: "PVP Disbyte",
    headerClassName: "header border-bottom",
    valueFormatter: (value) => {
      if (value == null) {
        return "";
      }
      return `$${formatearNumeroConComas(value)}`;
    },
    type: "number",
    minWidth: 110,
    flex: 1,
    resizable: false,
    sortable: false,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "pvpbuyer",
    headerName: "PVP Buyer",
    headerClassName: "header border-bottom",

    valueFormatter: (value) => {
      if (value == null) {
        return "";
      }
      return `$${formatearNumeroConComas(value)}`;
    },
    type: "number",
    minWidth: 90,
    flex: 1,
    resizable: false,
    sortable: false,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "discount",
    headerName: "Descuento",
    headerClassName: "header border-bottom",
    valueGetter: (value, row) => {
      // Convert the decimal value to a percentage
      // const discount = ((row.baseprice / row.pvp - 1) * 100).toFixed(2);
      // return discount === Number.isFinite(discount) ? discount : 0;
      const discount = (row.pvpdiscount * 100).toFixed(2);
      return discount;
    },
    valueSetter: (value, row) => {
      // const discount = ((row.baseprice / row.pvp - 1) * 100).toFixed(2);
      // return discount === Number.isFinite(discount) ? discount : 0;
      const discount = (row.pvpdiscount * 100).toFixed(2);
      return discount;
    },
    valueFormatter: (value) => {
      if (value == null) {
        return "";
      }
      return `${value}%`;
    },
    type: "number",
    width: 90,
    resizable: false,
    sortable: false,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "margen",
    headerName: "MG%",
    headerClassName: "header-special border-bottom",
    cellClassName: "cell-special",
    renderCell: (params) => {
      return <PercentageChip value={params.value} type="margen" />;
    },
    valueGetter: (value, row) => {
      // Convert the decimal value to a percentage
      return (row.margen * 100).toFixed(2);
    },
    valueFormatter: (value) => {
      if (value == null) {
        return "";
      }
      return `${value}%`;
    },
    type: "number",
    width: 90,
    resizable: false,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "roi",
    headerName: "ROI",
    renderCell: (params) => {
      return <PercentageChip value={params.value} type="roi" />;
    },
    valueGetter: (value, row) => {
      // Convert the decimal value to a percentage
      return (row.roi * 100).toFixed(2);
    },
    valueFormatter: (value) => {
      if (value == null) {
        return "";
      }
      return `${value}%`;
    },
    cellClassName: "cell-special",
    headerClassName: "header-special border-bottom",
    type: "number",
    width: 90,
    resizable: false,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "freeshipping",
    headerName: "FS",
    renderCell: (params) => {
      return <FreeShippingIndicator value={params.value} />;
    },
    cellClassName: "border-right",
    headerClassName: "header border-right border-bottom",
    type: "boolean",
    width: 60,
    resizable: false,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "usdcost_proyectado",
    headerName: "LC USD",
    headerClassName: "header border-bottom",
    renderCell: (params) => (
      <ColumnNumberField
        value={params.value}
        id={params.id}
        field={params.field}
        prefix="$"
        editable
      />
    ),
    renderEditCell: (params) => (
      <EditNumberInput {...params} hasFocus={params.hasFocus} />
    ),
    type: "number",
    editable: true,
    minWidth: 90,
    flex: 1,
    resizable: false,
    sortable: false,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "localcost_proyectado",
    headerName: "LC Local",
    headerClassName: "header border-bottom",
    renderCell: (params) => (
      <ColumnNumberField
        value={params.value}
        id={params.id}
        field={params.field}
        prefix="$"
      />
    ),
    type: "number",
    minWidth: 90,
    flex: 1,
    resizable: false,
    sortable: false,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "pvp_proyectado",
    headerName: "PVP + Shipping",
    headerClassName: "header border-bottom",
    renderHeader: () => <PvpHeader />,
    renderCell: (params) => (
      <ProjectedPvpField
        pvp={params.row.pvp}
        freeshipping={params.row.freeshipping_proyectado}
        freeshippingCost={params.row.freeshippingcostc}
        field={params.field}
        id={params.id}
      />
    ),
    type: "number",
    minWidth: 120,
    flex: 1,
    resizable: false,
    sortable: false,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "pvpdisbyte_proyectado",
    headerName: "PVP Disbyte",
    headerClassName: "header border-bottom",
    renderCell: (params) => (
      <ColumnNumberField
        value={params.value}
        id={params.id}
        field={params.field}
        prefix="$"
        editable
      />
    ),
    renderEditCell: (params) => (
      <EditNumberInput {...params} hasFocus={params.hasFocus} />
    ),
    editable: true,
    type: "number",
    minWidth: 110,
    flex: 1,
    resizable: false,
    sortable: false,
    headerAlign: "center",
    align: "center",
  },
  // {
  // 	field: 'pvpbuyer_proyectado',
  // 	headerName: 'PVP Buyer',
  // 	headerClassName: 'header border-bottom',

  // 	valueGetter: (value, row) => {
  // 		// Convert the decimal value to a percentage

  // 		if (value) {
  // 			return value;
  // 		}
  // 		return 0;
  // 	},
  // 	valueFormatter: (value) => {
  // 		if (value == null) {
  // 			return '';
  // 		}
  // 		return `$${formatearNumeroConComas(value)}`;
  // 	},
  // 	type: 'number',
  // 	minWidth: 90,
  // 	flex: 1,
  // 	resizable: false,
  // 	sortable: false,
  // 	headerAlign: 'center',
  // 	align: 'center',
  // },
  {
    field: "discount_proyectado",
    headerName: "Descuento",
    headerClassName: "header border-bottom",
    renderCell: (params) => (
      <ColumnNumberField
        value={params.value}
        id={params.id}
        field={params.field}
        suffix="%"
      />
    ),
    type: "number",
    width: 90,
    resizable: false,
    sortable: false,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "margen_proyectado",
    headerName: "MG%",
    headerClassName: "header-special border-bottom",
    cellClassName: "cell-special",
    renderCell: (params) => {
      return (
        <ProjectedPercentageField
          value={params.value}
          id={params.id}
          field={params.field}
          type="margen"
        />
      );
    },
    type: "number",
    width: 90,
    resizable: false,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "roi_proyectado",
    headerName: "ROI",
    renderCell: (params) => {
      return (
        <ProjectedPercentageField
          value={params.value}
          id={params.id}
          field={params.field}
          type="roi"
        />
      );
    },
    cellClassName: "cell-special",
    headerClassName: "header-special border-bottom",
    type: "number",
    width: 90,
    resizable: false,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "freeshipping_proyectado",
    headerName: "FS",
    renderCell: (params) => {
      return (
        <ProjectedFreeShipping
          value={params.value}
          id={params.id}
          field={params.field}
          params={params}
        />
      );
    },
    valueGetter: (value, row) => {
      return value;
    },
    cellClassName: "border-right",
    headerClassName: "header border-right border-bottom",
    type: "boolean",
    width: 60,
    resizable: false,
    headerAlign: "center",
    align: "center",
  },
];

export const columnGroupingModel = [
  {
    groupId: "Escenario actual",
    description: "",
    headerClassName: "border-right border-bottom",
    headerAlign: "center",
    align: "center",
    children: [
      {
        groupId: "Landed Cost",
        headerClassName: "border-right border-bottom",
        headerAlign: "center",
        align: "center",

        children: [{ field: "usdcost" }, { field: "localcost" }],
      },
      {
        groupId: "Pricing",
        headerClassName: "border-right border-bottom",
        headerAlign: "center",
        align: "center",

        children: [
          { field: "pvp" },
          { field: "pvpdisbyte" },
          { field: "pvpbuyer" },
          { field: "discount" },
          { field: "margen" },
          { field: "roi" },
          {
            field: "freeshipping",
          },
        ],
      },
    ],
  },
  {
    groupId: "Escenario proyectado",
    description: "",
    headerClassName: "border-right border-bottom",
    headerAlign: "center",
    align: "center",
    children: [
      {
        groupId: "Landed Cost",
        headerClassName: "border-right border-bottom",
        headerAlign: "center",
        align: "center",

        children: [
          { field: "usdcost_proyectado" },
          { field: "localcost_proyectado" },
        ],
      },
      {
        groupId: "Pricing",
        headerClassName: "border-right border-bottom",
        headerAlign: "center",
        align: "center",

        children: [
          { field: "pvp_proyectado" },
          { field: "pvpdisbyte_proyectado" },
          { field: "pvpbuyer_proyectado" },
          { field: "discount_proyectado" },
          { field: "margen_proyectado" },
          { field: "roi_proyectado" },
          {
            field: "freeshipping_proyectado",
          },
        ],
      },
    ],
  },
  {
    groupId: " ",
    description: "",
    headerClassName: "border-right border-bottom",

    children: [
      {
        groupId: "Información del producto",
        headerClassName: "border-right border-bottom",
        headerAlign: "center",
        align: "center",

        children: [
          { field: "thumbnail" },
          { field: "status" },
          { field: "sku" },
          { field: "listingtypename" },
          //   { field: "channel" },
        ],
      },
    ],
  },
];

export const tableStyles = {
  "& .MuiDataGrid-cell": {
    borderBottom: "1px solid #E0E0E0",
  },

  "& .border-right": {
    borderRight: "1px solid #E0E0E0",
  },
  "& .border-left": {
    borderLeft: "1px solid #E0E0E0",
  },
  "& .border-bottom": {
    borderBottom: "1px solid #E0E0E0",
  },
  "& .MuiDataGrid-columnHeader--emptyGroup": {
    borderBottom: "1px solid #E0E0E0",
  },
  "& .header": {
    backgroundColor: "#f3f4f6",
  },
  "& .header-special": {
    backgroundColor: "#e2e8f0",
  },
  "& .cell-special": {
    backgroundColor: "#f1f5f9",
  },
  "& .editable": {
    color: "rgba(0, 0, 0, 0.38)",
  },
  "& .MuiDataGrid-scrollbar--vertical": {
    display: "none",
  },
  "& .MuiDataGrid-filler": {
    height: "fit-content !important",
  },
  "& .MuiDataGrid-main": {
    paddingBottom: "16px !important",
  },
  "& .no-padding": {
    padding: "0 !important",
  },
  "& .cell-cat": {
    backgroundColor: "#FFE0B2 !important",
  },
  "& .cell-hij": {
    backgroundColor: "#f9cfcf !important",
  },
  "& .triangle-top-left-cat": {
    width: "0",
    height: "0",
    borderBottom: "16px solid transparent",
    borderLeft: "16px solid orange",
  },
  "& .triangle-top-left-error": {
    width: "0",
    height: "0",
    borderBottom: "16px solid transparent",
    borderLeft: "16px solid red",
  },
  "& .triangle-bottom-left-error": {
    width: "0",
    height: "0",
    borderTop: "16px solid transparent",
    borderLeft: "16px solid red",
  },
  "& .MuiToolbar-root": {
    padding: "0 !important",
  },

  height: "calc(100vh - 325px)",
  // height: '100%',
};

export const slots = {
  noRowsOverlay: EmptyTable,
  noResultsOverlay: EmptyTable,
};
